import React, { useState, useRef, useEffect } from 'react';

const SearchableSelect = ({ 
  options = [], 
  value = '', 
  selectedValue = '',
  onChange, 
  placeholder = "Rechercher...",
  displayField = "type",
  valueField = "type",
  disabled = false,
  multiple = false,
  selectedValues = [],
  onMultipleChange,
  className = "",
  allowCreate = false,
  onCreateNew,
  createNewLabel = "+ Créer",
  renderOption = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Use selectedValue if provided, otherwise use value
  const currentValue = selectedValue || value;

  // Filter options based on search term
  const filteredOptions = options.filter(option => {
    const searchText = typeof option === 'string' ? option : option[displayField];
    return searchText && searchText.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Check if search term matches any existing option
  const searchTermExists = filteredOptions.some(option => {
    const optionText = typeof option === 'string' ? option : option[displayField];
    return optionText.toLowerCase() === searchTerm.toLowerCase();
  });

  const showCreateOption = allowCreate && searchTerm.trim() !== '' && !searchTermExists;

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleOptionSelect = (option) => {
    const optionValue = typeof option === 'string' ? option : option[valueField];
    
    if (multiple) {
      let newSelectedValues;
      if (selectedValues.includes(optionValue)) {
        newSelectedValues = selectedValues.filter(v => v !== optionValue);
      } else {
        newSelectedValues = [...selectedValues, optionValue];
      }
      onMultipleChange(newSelectedValues);
    } else {
      onChange(optionValue);
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const handleCreateNew = async () => {
    if (!searchTerm.trim() || !onCreateNew) return;
    
    setIsCreating(true);
    const success = await onCreateNew(searchTerm.trim());
    setIsCreating(false);
    
    if (success) {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const getDisplayValue = () => {
    if (multiple) {
      if (selectedValues.length === 0) return '';
      if (selectedValues.length === 1) {
        const selected = options.find(opt => 
          (typeof opt === 'string' ? opt : opt[valueField]) === selectedValues[0]
        );
        return typeof selected === 'string' ? selected : (selected ? selected[displayField] : selectedValues[0]);
      }
      return `${selectedValues.length} sélectionné(s)`;
    } else {
      if (!currentValue) return '';
      const selected = options.find(opt => 
        (typeof opt === 'string' ? opt : opt[valueField]) === currentValue
      );
      return typeof selected === 'string' ? selected : (selected ? selected[displayField] : currentValue);
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Input Field */}
      <div
        className={`w-full p-3 border border-gray-300 rounded-lg bg-white cursor-pointer flex items-center justify-between ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'hover:border-gray-400'
        } ${isOpen ? 'ring-2 ring-green-500 border-green-500' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className={`flex-1 ${!getDisplayValue() ? 'text-gray-500' : 'text-gray-900'}`}>
          {getDisplayValue() || placeholder}
        </span>
        <svg 
          className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
          {/* Search Input */}
          <div className="p-3 border-b border-gray-200">
            <input
              ref={inputRef}
              type="text"
              placeholder={`Rechercher dans ${options.length} options...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
              autoFocus
            />
          </div>

          {/* Options List */}
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 && !showCreateOption ? (
              <div className="p-3 text-gray-500 text-center">
                Aucun résultat trouvé pour "{searchTerm}"
              </div>
            ) : (
              <>
                {filteredOptions.map((option, index) => {
                  const optionValue = typeof option === 'string' ? option : option[valueField];
                  const optionDisplay = typeof option === 'string' ? option : option[displayField];
                  const optionCategory = typeof option === 'object' ? option.category : null;
                  const isSelected = multiple 
                    ? selectedValues.includes(optionValue)
                    : currentValue === optionValue;

                  return (
                    <div
                      key={`${optionValue}-${index}`}
                      className={`p-3 cursor-pointer hover:bg-gray-50 flex items-center justify-between ${
                        isSelected ? 'bg-green-50 text-green-700' : 'text-gray-900'
                      }`}
                      onClick={() => handleOptionSelect(option)}
                    >
                      <div className="flex-1">
                        {renderOption ? (
                          renderOption(option)
                        ) : (
                          <>
                            <div className="font-medium">{optionDisplay}</div>
                            {optionCategory && (
                              <div className="text-xs text-gray-500 mt-1">
                                <span className="px-2 py-0.5 bg-gray-100 rounded text-gray-600">
                                  {optionCategory}
                                </span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      {multiple && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          className="ml-2 w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                        />
                      )}
                      {!multiple && isSelected && (
                        <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  );
                })}
                
                {/* Create new option button */}
                {showCreateOption && (
                  <div
                    className="p-3 cursor-pointer hover:bg-blue-50 border-t border-gray-200 bg-blue-50/50 text-blue-700 font-medium flex items-center gap-2"
                    onClick={handleCreateNew}
                  >
                    {isCreating ? (
                      <>
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Création en cours...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        <span>{createNewLabel} : "{searchTerm}"</span>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Selected count for multiple selection */}
          {multiple && selectedValues.length > 0 && (
            <div className="p-3 border-t border-gray-200 bg-gray-50 text-sm text-gray-600">
              {selectedValues.length} élément(s) sélectionné(s)
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;